import { openDB } from 'idb';

const DB_NAME = 'documentFileCache';
const STORE_NAME = 'documentFiles';
const CACHE_EXPIRATION_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds

// Initialize or open the database
const initDB = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
        store.createIndex('timestamp', 'timestamp'); // Index for timestamp
      }
    },
  });
};

// Add or update a file in the cache
export const addDocumentFileToCache = async (url: string, blob: Blob) => {
  const db = await initDB();
  const timestamp = Date.now();
  await db.put(STORE_NAME, { url, blob, timestamp });
};

// Fetch a file from the cache
export const getDocumentFileFromCache = async (
  url: string
): Promise<Blob | null> => {
  const db = await initDB();
  const cachedFile = await db.get(STORE_NAME, url);

  if (cachedFile) {
    // Check expiration
    if (Date.now() - cachedFile.timestamp < CACHE_EXPIRATION_TIME) {
      return cachedFile.blob;
    } else {
      // Remove expired entry
      await db.delete(STORE_NAME, url);
    }
  }
  return null;
};

// Remove one file from the cache
export const removeDocumentFile = async (url: string) => {
  const db = await initDB();
  await db.delete(STORE_NAME, url);
};

// Remove expired files from the cache
export const clearExpiredDocumentFiles = async () => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);

  const now = Date.now();
  let cursor = await store.openCursor();

  while (cursor) {
    if (now - cursor.value.timestamp >= CACHE_EXPIRATION_TIME) {
      await cursor.delete();
    }
    cursor = await cursor.continue();
  }
};
